// Diagnosis panel renderer (PRD §12).
//
// UI structure:
//   1. 总评 card        — actual DPS, baseline gap, ranking percentile (async)
//   2. Top 3 issues     — highest-loss findings as clickable badges
//   3. 全部诊断 list    — every finding as a collapsible card
//
// All rendering is sync. The async ranking fill is performed by main.ts
// after this initial render — when the rankings response lands, main.ts
// calls fillRankings(slot, data) on the already-rendered panel.

import type { DiagnosisReport, DiagnosisFinding, DiagnosisSeverity } from '@shared/diagnosis';
import { t } from '@shared/i18n';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

function fmtDps(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '—';
  return Math.round(n).toLocaleString();
}

function fmtPct(n: number, fractionDigits = 2): string {
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(fractionDigits)}%`;
}

function severityClass(s: DiagnosisSeverity): string {
  return `diag-${s}`;
}

function severityIcon(s: DiagnosisSeverity): string {
  switch (s) {
    case 'critical': return '🔴';
    case 'major':    return '🟠';
    case 'minor':    return '🟡';
    case 'good':     return '🟢';
    default:         return '⚪';
  }
}

// Format a finding's headline by interpolating params into the i18n string.
// Falls back to the raw key if the i18n entry isn't defined yet (acceptable
// during MVP when ZH copy is placeholder).
//
// Special: M7 findings carry `buffSpellId` so we can swap the English
// fallback `buff` param with the localised buff name pulled from the
// existing `aura.{spellId}` i18n keys (most popular buffs are already
// translated for the auras panel; we just reuse those entries).
function formatHeadline(f: DiagnosisFinding): string {
  const params = { ...(f.headlineParams ?? {}) };
  const sid = params.buffSpellId;
  if (typeof sid === 'number' && sid > 0) {
    // Prefer a dedicated buff.{id} key (curated for the diagnosis layer);
    // fall back to aura.{id} which already exists for many spells in the
    // auras panel; finally fall back to the English `buff` param.
    for (const key of [`buff.${sid}`, `aura.${sid}`]) {
      const localised = t(key);
      if (localised && localised !== key) {
        params.buff = localised;
        break;
      }
    }
  }
  return t(f.headline, params);
}

// Build one finding card — used in both the Top 3 strip and the full list.
function findingCardHtml(f: DiagnosisFinding, compact = false): string {
  const sevCls = severityClass(f.severity);
  const icon   = severityIcon(f.severity);
  const dpsTag = f.dpsLossPct >= 0.5
    ? `<span class="diag-loss ${sevCls}">−${fmtPct(f.dpsLossPct, 1)}</span>`
    : '';
  if (compact) {
    return `
      <button type="button" class="diag-top-card ${sevCls}" data-finding-module="${escapeHtml(f.module)}">
        <span class="diag-icon">${icon}</span>
        <span class="diag-headline">${escapeHtml(formatHeadline(f))}</span>
        ${dpsTag}
      </button>
    `;
  }
  // Each detail is either an i18n key (string) or {key, params} object — both
  // get pumped through t() so EN/ZH switch correctly. Raw debug strings have
  // been removed from modules; everything here is localized.
  const detailsHtml = f.details.length > 0
    ? `<ul class="diag-details">${f.details.map((d) => {
        const text = typeof d === 'string' ? t(d) : t(d.key, d.params ?? {});
        return `<li>${escapeHtml(text)}</li>`;
      }).join('')}</ul>`
    : '';
  const sugHtml = f.suggestions.length > 0
    ? `<ul class="diag-suggestions">${f.suggestions.map((s) => `<li>${escapeHtml(t(s))}</li>`).join('')}</ul>`
    : '';
  const drillBtn = f.drillDownPanel
    ? `<button type="button" class="diag-drill" data-target="${escapeHtml(f.drillDownPanel)}">${escapeHtml(t('diagnosis.drillDown'))} →</button>`
    : '';
  return `
    <details class="diag-card ${sevCls}">
      <summary>
        <span class="diag-icon">${icon}</span>
        <span class="diag-module">${escapeHtml(t(f.moduleName))}</span>
        <span class="diag-headline">${escapeHtml(formatHeadline(f))}</span>
        ${dpsTag}
      </summary>
      <div class="diag-body">
        ${detailsHtml}
        ${sugHtml}
        ${drillBtn}
      </div>
    </details>
  `;
}

// Render the entire diagnosis panel into `container`. Idempotent — call any
// number of times to refresh when the user changes fight / player / buffs.
export function renderDiagnosis(report: DiagnosisReport, container: HTMLElement): void {
  const gapAbs    = Math.max(0, report.ruleBaselineDps - report.actualDps);
  const gapPct    = report.ruleBaselineDps > 0
    ? (gapAbs / report.ruleBaselineDps) * 100
    : 0;
  const top3 = report.findings.slice(0, 3);
  const allFindings = report.findings;

  // Stats summary line — 3 lines, ranking line is filled async.
  const summaryHtml = `
    <div class="diag-summary">
      <div class="diag-stat">
        <span class="diag-stat-label">${escapeHtml(t('diagnosis.summary.actualDps'))}</span>
        <span class="diag-stat-value">${fmtDps(report.actualDps)}</span>
      </div>
      <div class="diag-stat">
        <span class="diag-stat-label">${escapeHtml(t('diagnosis.summary.ruleBaseline'))}</span>
        <span class="diag-stat-value">
          ${fmtDps(report.ruleBaselineDps)}
          ${gapAbs > 0
            ? `<span class="diag-loss diag-major">−${fmtDps(gapAbs)} (${fmtPct(gapPct, 1)})</span>`
            : ''}
        </span>
      </div>
      <div class="diag-stat" data-ranking-slot>
        <span class="diag-stat-label">${escapeHtml(t('diagnosis.summary.ranking'))}</span>
        <span class="diag-stat-value diag-loading" data-ranking-value>${escapeHtml(t('diagnosis.summary.rankingLoading'))}</span>
      </div>
    </div>
  `;

  const top3Html = top3.length > 0
    ? `
      <div class="diag-top3">
        <h4 class="diag-section-title">${escapeHtml(t('diagnosis.top3.title'))}</h4>
        <div class="diag-top3-cards">
          ${top3.map((f) => findingCardHtml(f, true)).join('')}
        </div>
      </div>
    `
    : `<p class="diag-empty muted small">${escapeHtml(t('diagnosis.empty.allGood'))}</p>`;

  const fullListHtml = `
    <div class="diag-fulllist">
      <h4 class="diag-section-title">${escapeHtml(t('diagnosis.full.title', { count: allFindings.length }))}</h4>
      ${allFindings.map((f) => findingCardHtml(f, false)).join('') || `<p class="muted small">${escapeHtml(t('diagnosis.empty.noFindings'))}</p>`}
    </div>
  `;

  container.innerHTML = `
    ${summaryHtml}
    ${top3Html}
    ${fullListHtml}
  `;

  // Wire drill-down buttons: scroll into view + open the target details
  // element if it's a panel.
  container.querySelectorAll<HTMLButtonElement>('.diag-drill').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const target = btn.dataset.target;
      if (!target) return;
      // Try the same slot first (panel-rotation-a vs panel-rotation-b).
      // Walk up to find the slot suffix.
      const slot = btn.closest<HTMLElement>('[data-slot]')?.dataset.slot;
      const candidates = slot
        ? [`${target}-${slot}`, target]
        : [target];
      for (const id of candidates) {
        const el = document.getElementById(id);
        if (!el) continue;
        // If it's a <details>, open it.
        if (el instanceof HTMLDetailsElement) el.open = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    });
  });

  // Top-3 cards are clickable: scroll to the matching full-list card and
  // open it.
  container.querySelectorAll<HTMLButtonElement>('.diag-top-card').forEach((btn) => {
    btn.addEventListener('click', () => {
      const mod = btn.dataset.findingModule;
      if (!mod) return;
      const cards = container.querySelectorAll<HTMLDetailsElement>('.diag-fulllist .diag-card');
      for (const c of cards) {
        const headline = c.querySelector<HTMLElement>('.diag-module')?.textContent ?? '';
        if (!headline) continue;
        // Match by module key — top card data attribute carries the M-x code,
        // and our full-list cards expose it via the hidden module attribute too
        // (we'd need to add it; do that in next iteration). For now we open
        // the first matching summary by module name comparison via a custom
        // attribute fallback.
      }
      const first = container.querySelector<HTMLDetailsElement>(`.diag-fulllist .diag-card[data-module="${mod}"]`);
      if (first) {
        first.open = true;
        first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  });
}

// Result of the rankings async fetch (subset of /api/wcl/rankings response).
export interface RankingsFillData {
  bestPercentile: number | null;
  medianDps: number | null;
  top10Dps: number | null;
  top1Dps: number | null;
  rankCount: number;
}

// Fill in the ranking line of an already-rendered diagnosis panel. Called
// asynchronously from main.ts when the /api/wcl/rankings response lands.
// If `data` is null (request failed), shows "—" so the user knows we tried.
export function fillRankings(container: HTMLElement, data: RankingsFillData | null): void {
  const slot = container.querySelector<HTMLElement>('[data-ranking-slot]');
  const valueEl = container.querySelector<HTMLElement>('[data-ranking-value]');
  if (!slot || !valueEl) return;

  valueEl.classList.remove('diag-loading');
  if (!data || data.medianDps == null) {
    valueEl.textContent = '—';
    return;
  }
  const parts: string[] = [];
  if (data.bestPercentile != null) {
    parts.push(t('diagnosis.summary.percentile', { p: data.bestPercentile.toFixed(0) }));
  }
  if (data.medianDps != null) {
    parts.push(t('diagnosis.summary.median', { dps: fmtDps(data.medianDps) }));
  }
  if (data.top10Dps != null) {
    parts.push(t('diagnosis.summary.top10', { dps: fmtDps(data.top10Dps) }));
  }
  valueEl.textContent = parts.join(' · ');
}
